import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { CircularProgress, Box } from '@material-ui/core';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useSnackbar } from 'notistack';

import { getCategoryCountHistory } from '../../requests/api/categorycounts';

const HISTORY_WINDOW_DAYS = 90;

// Mismo componente de gráfico (Highcharts stockChart) que "Productos Scrapeados"
// (ver Scraper/ScraperStatistics/Graph.js), adaptado a una sola serie: acá no hay
// "objetivo calculado" ni "objetivo teórico", solo la cantidad medida por nodo.
//
// Desde/Hasta parten en la ventana de 90 días por defecto pero quedan editables
// (mismo patrón DesktopDatePicker que Scraper/ScraperStatistics/Results.js) para
// que el usuario pueda ver un rango distinto sin salir del gráfico.
function CategoryCountGraph({
  groupBy, nodeId, supermarketId, title,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [endDate, setEndDate] = useState(() => Date.now());
  const [startDate, setStartDate] = useState(
    () => Date.now() - HISTORY_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  );

  useEffect(() => {
    let active = true;

    const fetchHistory = async () => {
      const response = await getCategoryCountHistory(
        groupBy, nodeId, supermarketId, startDate, endDate,
      );

      if (!active) return;

      if (response.success) {
        const rows = response.data?.data?.history || [];
        setData(rows.map((row) => [Date.parse(row.date_trunc), row.count]));
      } else {
        enqueueSnackbar('Error al cargar el historial de conteo.', { variant: 'error' });
        setData([]);
      }
    };

    fetchHistory();

    return () => {
      active = false;
    };
  }, [groupBy, nodeId, supermarketId, startDate, endDate, enqueueSnackbar]);

  const dateRangePicker = (
    <Grid container spacing={1} justifyContent="flex-end">
      <Grid item>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            label="Desde"
            inputFormat="dd/MM/yyyy"
            value={startDate}
            onChange={(newDate) => setStartDate(newDate.valueOf())}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            label="Hasta"
            inputFormat="dd/MM/yyyy"
            value={endDate}
            onChange={(newDate) => setEndDate(newDate.valueOf())}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        </LocalizationProvider>
      </Grid>
    </Grid>
  );

  if (data === null) {
    return (
      <Box>
        <Box mb={2}>{dateRangePicker}</Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const options = {
    title: {
      text: title,
    },
    time: {
      useUTC: false,
      timezone: 'America/Santiago',
    },
    series: [
      {
        data,
        name: 'Cantidad de productos',
      },
    ],
    plotOptions: {
      series: {
        type: 'spline',
        marker: {
          enabled: null,
          radius: 3,
        },
        tooltip: {
          valueDecimals: 0,
          dateTimeLabelFormats: {
            millisecond: '%b %e',
          },
        },
      },
    },
    yAxis: [{
      offset: 20,
      resize: {
        enabled: true,
      },
      labels: {
        x: -15,
        style: {
          color: '#000',
          position: 'absolute',
        },
        align: 'left',
      },
    }],
    chart: {
      height: 400,
      zoomType: '',
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      minRange: 1,
      labels: {
        format: '{value:%d %b}',
        rotation: -50,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
  };

  return (
    <Box>
      <Box mb={2}>{dateRangePicker}</Box>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType={'stockChart'}
        options={options}
      />
    </Box>
  );
}

CategoryCountGraph.propTypes = {
  groupBy: PropTypes.oneOf(['supermarket', 'category', 'subcategory']).isRequired,
  nodeId: PropTypes.number,
  supermarketId: PropTypes.number,
  title: PropTypes.string.isRequired,
};

CategoryCountGraph.defaultProps = {
  nodeId: null,
  supermarketId: null,
};

export default CategoryCountGraph;
